import { SYN_API_URLS } from '@/constants/api';
import { PORTFOLIO_PAGE_POLLING_INTERVAL } from '@/constants/polling';
import { QUERY_KEYS } from '@/constants/query';
import { queryClient } from '@/global';
import type { ITotalVolume } from '@/types/portfolio';
import { axiosGet } from '@/utils/axios';
import { FetchPortfolioParam } from '@synfutures/sdks-perp';
import { useQuery } from '@tanstack/react-query';
import { FetchInstrumentFromChainResponse } from '../futures/types';
import { useSDK } from '../web3/hook';
import { fetchPortfolioListFromApi, fetchPortfolioListFromChain } from './api';
export const useFetchSinglePortfolioFromApi = (
  chainId: number | undefined,
  userAddr: string | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
) => {
  const sdk = useSDK(chainId);
  return useQuery({
    queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr, instrumentAddr, expiry),
    queryFn: async () => {
      if (chainId && userAddr)
        try {
          // First try to get from API
          const apiResult = await fetchPortfolioListFromApi(chainId, userAddr, instrumentAddr, expiry);
          return apiResult;
          // throw new Error('API returned no data');
        } catch (apiError) {
          console.warn('API request failed, falling back to chain:', apiError);
          try {
            if (sdk) {
              let portfolioParams: FetchPortfolioParam[] | undefined = undefined;
              if (instrumentAddr && expiry) {
                portfolioParams = [{ instrumentAddr, expiry, traderAddr: userAddr }];
              }
              const instrument = queryClient.getQueryData<FetchInstrumentFromChainResponse>(
                QUERY_KEYS.FUTURES.INSTRUMENT(chainId, instrumentAddr),
              );
              // If API fails, fall back to chain request
              const chainResult = await fetchPortfolioListFromChain({
                sdk,
                userAddr,
                chainId,
                portfolioParams: portfolioParams,
              });
              return {
                instruments: instrument ? [instrument] : [],
                portfolios: chainResult || [],
              };
            }
          } catch (chainError) {
            console.error('Both API and chain requests failed:', chainError);
            throw new Error('Failed to fetch instrument from both API and chain');
          }
        }
      return undefined;
    },
    refetchInterval: PORTFOLIO_PAGE_POLLING_INTERVAL,
    enabled: !!chainId && !!userAddr && !!instrumentAddr && !!expiry,
  });
};

export const useFetchPortfolioListFromApi = (chainId: number | undefined, userAddr: string | undefined) => {
  // const sdk = useSDK(chainId);
  return useQuery({
    queryKey: QUERY_KEYS.PORTFOLIO.PORTFOLIO(chainId, userAddr),
    queryFn: async () => {
      if (chainId && userAddr)
        try {
          // First try to get from API

          const apiResult = await fetchPortfolioListFromApi(chainId, userAddr);

          return apiResult;
          // throw new Error('API returned no data');
        } catch (apiError) {
          console.error('API request failed, falling back to chain:', apiError);
          // try {
          //   if (sdk) {
          //     let portfolioParams: FetchPortfolioParam[] | undefined = undefined;
          //     let instruments: Instrument[] = [];
          //     const allPaticipatedFutures = await getAllParticipatedFuturesFromChain({
          //       chainId,
          //       sdkContext: sdk,
          //       userAddr,
          //     });

          //     if (allPaticipatedFutures) {
          //       instruments = allPaticipatedFutures;
          //       portfolioParams = allPaticipatedFutures.reduce((acc, instrument) => {
          //         instrument.amms.forEach((amm) => {
          //           acc.push({
          //             traderAddr: userAddr,
          //             instrumentAddr: instrument.instrumentAddr,
          //             expiry: amm.expiry,
          //           });
          //         });

          //         return acc;
          //       }, [] as FetchPortfolioParam[]);
          //     }

          //     // If API fails, fall back to chain request
          //     const chainResult = await fetchPortfolioListFromChain({
          //       sdk,
          //       userAddr,
          //       chainId,
          //       portfolioParams: portfolioParams,
          //     });
          //     return {
          //       instruments: instruments || [],
          //       portfolios: chainResult || [],
          //     };
          //   }
          // } catch (chainError) {
          //   console.error('Both API and chain requests failed:', chainError);
          //   throw new Error('Failed to fetch instrument from both API and chain');
          // }
          return null;
        }
      return null;
    },
    refetchInterval: PORTFOLIO_PAGE_POLLING_INTERVAL,
    enabled: !!chainId && !!userAddr,
  });
};

/**
 * Fetch user's total volume via API using React Query.
 * Replaces Redux thunk getTotalVolume with a query hook and 30s refetch interval.
 */
export const useFetchTotalVolume = (chainId: number | undefined, userAddr: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.PORTFOLIO.USER_VOLUME(chainId, userAddr),
    queryFn: async (): Promise<ITotalVolume | null> => {
      if (!chainId || !userAddr) return null;
      try {
        const result = await axiosGet({
          url: SYN_API_URLS.PORTFOLIO.USER_VOLUME,
          config: { params: { userAddress: userAddr, chainId } },
        });
        return result?.data?.data as ITotalVolume | null;
      } catch (error) {
        console.warn('total volume api error:', error);
        return null;
      }
    },
    refetchInterval: 30000,
    enabled: !!chainId && !!userAddr,
  });
};
